import axios from 'axios'
import { attachAuthInterceptors } from '../auth/axiosAuth'

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
})

// Same in-memory access token and refresh-on-401 handling as each app's own API client
attachAuthInterceptors(api)

export interface UpdateProfileRequest {
  name?: string
  email?: string
}

export interface UpdatePasswordRequest {
  currentPassword: string
  newPassword: string
}

export interface UpdateAvatarRequest {
  avatarUrl: string
}

export { api }

export const userService = {
  /**
   * Update user profile information (name, email)
   */
  updateProfile: async (profileData: UpdateProfileRequest) => {
    try {
      const { data } = await api.patch('/users/me', profileData)
      return data
    } catch (error: any) {
      throw new Error(`Failed to update profile: ${error.response?.data?.error || error.message}`)
    }
  },

  /**
   * Update user password
   */
  updatePassword: async (passwordData: UpdatePasswordRequest) => {
    try {
      const { data } = await api.patch('/users/me/password', passwordData)
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message)
    }
  },

  /**
   * Upload cropped avatar image to backend.
   * Standardizes on /users/me/avatar which is the correct system-wide endpoint.
   */
  uploadCroppedAvatar: async (
    croppedImageBase64: string,
  ): Promise<{ avatarUrl: string; message: string }> => {
    try {
      const { data } = await api.post('/users/me/avatar', {
        croppedImage: croppedImageBase64,
      })
      return data
    } catch (error: any) {
      throw new Error(`Failed to upload avatar: ${error.response?.data?.error || error.message}`)
    }
  },

  /**
   * Manually update user avatar with a URL
   */
  updateAvatarUrl: async (avatarUrl: string) => {
    try {
      const { data } = await api.patch('/users/me/avatar', { avatarUrl })
      return data
    } catch (error: any) {
      throw new Error(`Failed to update avatar: ${error.response?.data?.error || error.message}`)
    }
  },

  /**
   * Close (deactivate) the current user's account. Blocks login immediately;
   * the account is de-identified after the server's retention window.
   */
  closeAccount: async (
    reason?: string,
  ): Promise<{ message: string; purge_scheduled_at: string }> => {
    try {
      const { data } = await api.post('/users/me/close', { reason })
      return data
    } catch (error: any) {
      throw new Error(`Failed to close account: ${error.response?.data?.error || error.message}`)
    }
  },
}
